import { useRef, useState } from "react";
import { API } from "@/lib/api";

const SID = "rs_zoya_session";
export const zoyaSession = () => {
  let s = localStorage.getItem(SID);
  if (!s) {
    s = (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(16).slice(2)}`).replace(/-/g, "");
    localStorage.setItem(SID, s);
  }
  return s;
};

const post = (path, body) =>
  fetch(`${API}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

// Streams Zoya Ma'am's answer (SSE) and calls onText with the growing text.
export async function askZoya(body, onText, onMeta, onSuggest) {
  const res = await post("/classroom/ask", body);
  if (res.status === 429) throw new Error("busy");
  if (!res.ok || !res.body) throw new Error("failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", text = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      const line = ev.trim();
      if (!line.startsWith("data:")) continue;
      const d = JSON.parse(line.slice(5));
      if (d.error) throw new Error("failed");
      if (d.guard) onMeta?.(d.guard, d);
      if (d.t) { text += d.t; onText(text); }
      if (d.suggest) onSuggest?.(d.suggest);
    }
  }
  return text.trim();
}

export async function zoyaVoiceUrl(text, lang = "hi") {
  const res = await post("/classroom/speak", { text: text.slice(0, 1200), lang });
  if (!res.ok) throw new Error("voice");
  return URL.createObjectURL(await res.blob());
}

// One audio channel for Zoya Ma'am so clips never talk over each other.
let ctx = null;
export const primeAudio = () => {
  try { ctx = ctx || new (window.AudioContext || window.webkitAudioContext)(); if (ctx.state === "suspended") ctx.resume(); } catch { /* no WebAudio */ }
};
// Live voice loudness (0..1) for Zoya's audio-reactive glow; WebAudio analyser per clip, shared context.
const meter = (a, levelRef) => {
  try {
    primeAudio();
    if (ctx.state !== "running") return; // never route audio through a suspended context (it would be silent)
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaElementSource(a).connect(an);
    an.connect(ctx.destination);
    const buf = new Uint8Array(an.fftSize);
    const tick = () => {
      if (a.paused || a.ended) { levelRef.current = 0; return; }
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) sum += ((buf[i] - 128) / 128) ** 2;
      levelRef.current = Math.min(1, Math.sqrt(sum / buf.length) * 4);
      requestAnimationFrame(tick);
    };
    a.addEventListener("play", () => requestAnimationFrame(tick));
  } catch { levelRef.current = 0; }
};

// Every Zoya clip goes through one channel: a new line cuts the old one, and stop() also cancels voices still being fetched.
export const useZoyaAudio = () => {
  const ref = useRef(null);
  const gen = useRef(0);
  const levelRef = useRef(0);
  const [speaking, setSpeaking] = useState(false);
  const start = (src, onEnd) => {
    const old = ref.current;
    if (old) { old.onpause = old.onended = null; old.pause(); }
    const a = new Audio(src);
    ref.current = a;
    meter(a, levelRef);
    a.onplay = () => setSpeaking(true);
    a.onpause = () => setSpeaking(false);
    a.onended = () => { setSpeaking(false); onEnd?.(); };
    a.play().catch(() => { setSpeaking(false); onEnd?.(); });
  };
  const play = (src, onEnd) => { gen.current += 1; start(src, onEnd); };
  const say = (text, lang = "hi", fallback = null, onEnd = undefined) => {
    const g = ++gen.current;
    ref.current?.pause();
    zoyaVoiceUrl(text, lang).then((u) => { if (g === gen.current) start(u, onEnd); })
      .catch(() => { if (g !== gen.current) return; if (fallback) start(fallback, onEnd); else onEnd?.(); });
  };
  const stop = () => { gen.current += 1; ref.current?.pause(); };
  return { play, say, stop, speaking, levelRef };
};

export const clip = (name) => `/classroom/voice/${name}.mp3`;

const REC_LANG = { hi: "hi-IN", en: "en-IN", bn: "bn-IN", pa: "pa-IN", gu: "gu-IN", mr: "mr-IN", te: "te-IN", ta: "ta-IN", kn: "kn-IN", ml: "ml-IN" };
// Browser speech-to-text (free, on-device / browser service); hidden where unsupported.
export const useSpeechInput = (lang, onText) => {
  const Rec = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const [listening, setListening] = useState(false);
  const start = () => {
    if (!Rec || listening) return;
    const r = new Rec();
    r.lang = REC_LANG[lang] || "en-IN";
    r.interimResults = false;
    r.onresult = (e) => onText(e.results[0][0].transcript);
    r.onend = r.onerror = () => setListening(false);
    setListening(true);
    r.start();
  };
  return { supported: Boolean(Rec), listening, start };
};
